import { ChevronDown, Plus, Building2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, identicon } from "@/components/ui/avatar";

export interface OrgSummary {
  id: string;
  slug: string;
  name: string;
}

/**
 * Org chip in the header breadcrumb. Renders the current org's avatar +
 * name; clicking opens a dropdown listing all orgs the user belongs to.
 * Does NOT show the project — that's a separate ProjectSwitcher rendered
 * by the Header so each segment is independently clickable.
 */
export function OrgSwitcher({ orgs }: { orgs: OrgSummary[] }) {
  const params = useParams<{ org?: string; project?: string }>();
  const nav = useNavigate();
  const current = orgs.find((o) => o.slug === params.org) ?? orgs[0];
  if (!current) return null;
  const ic = identicon(current.slug);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-2 h-7 px-2 rounded-md hover:bg-subtle transition-colors -ml-1 max-w-[200px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
          aria-label={`Switch organization (current: ${current.name})`}
        >
          <Avatar className="h-5 w-5 rounded-md">
            <AvatarFallback className="rounded-md text-white" style={{ background: ic.bg }}>
              {ic.initials}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-semibold truncate">{current.name}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-64">
        <DropdownMenuLabel>Organizations</DropdownMenuLabel>
        {orgs.map((o) => {
          const i = identicon(o.slug);
          return (
            <DropdownMenuItem key={o.id} onSelect={() => nav(`/orgs/${o.slug}`)}>
              <Avatar className="h-5 w-5 rounded-md">
                <AvatarFallback className="rounded-md text-white" style={{ background: i.bg }}>
                  {i.initials}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{o.name}</span>
              <span className="ml-auto text-2xs text-subtle-foreground font-mono">{o.slug}</span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/orgs/new">
            <Plus className="h-3.5 w-3.5" /> New organization
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/orgs">
            <Building2 className="h-3.5 w-3.5" /> Manage organizations
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
// Provide a tiny fallback button used by the Header before orgs load
export function OrgSwitcherShell() {
  return (
    <Button variant="ghost" size="sm" className="h-7 px-2 gap-2 -ml-1 opacity-60">
      <div className="h-5 w-5 rounded-md bg-subtle animate-pulse" />
      <span className="text-sm font-semibold tracking-tight">Loading…</span>
    </Button>
  );
}
